import { Route } from 'react-router-dom';
import { AuditLogs } from '../components/audit-logs/AuditLogs';
import { BillingExporter } from '../components/billing/BillingExporter';
import { BillingExportHistory } from '../components/billing/BillingExportHistory';
import { BillingExportStats } from '../components/billing/BillingExportStats';
import { Claims } from '../components/claims/Claims';
import { ClaimsMetricsPage } from '../components/claims/ClaimsMetricsPage';
import SingleClaim from '../components/claims/SingleClaim';
import { CommunicationsCenter } from '../components/communications-center/CommunicationCenter';
import CouponsManager from '../components/coupons/Coupons';
import BulkReprocess from '../components/document/BulkReprocess/BulkReprocess';
import DocumentDetails from '../components/document/Details/DocumentDetails';
import Documents from '../components/document/Document';
import DocumentUploader from '../components/document/DocumentUploader';
import DocumentSettings from '../components/document/Settings/DocumentSettings';
import DocumentTypeDetail from '../components/document/Settings/DocumentType/DocumentTypeDetail';
import LlmPromptDetail from '../components/document/Settings/LlmPrompt/LlmPromptDetail';
import { ACEImporter } from '../components/eligibility/ace/ACEImporter';
import { InsurancePlansCreateOrEdit } from '../components/eligibility/insurancePlans/InsurancePlans';
import { PayersCreate } from '../components/eligibility/payers/Payers/PayersCreate';
import { PayersUpdate } from '../components/eligibility/payers/Payers/PayersUpdate';
import { TaskShowUpdate } from '../components/eligibility/tasks/Tasks';
import { MedicalSetup } from '../components/medical-setup/MedicalSetup';
import { CreateCondition } from '../components/medical-setup/tabs/Conditions/CreateCondition';
import { EditCondition } from '../components/medical-setup/tabs/Conditions/EditCondition';
import { ViewCondition } from '../components/medical-setup/tabs/Conditions/ViewCondition';
import { CreateLaboratoryTest } from '../components/medical-setup/tabs/LaboratoryTests/CreateLaboratoryTest';
import { EditLaboratoryTest } from '../components/medical-setup/tabs/LaboratoryTests/EditLaboratoryTest';
import { ViewLaboratoryTest } from '../components/medical-setup/tabs/LaboratoryTests/ViewLaboratoryTest';
import { CreateMedication } from '../components/medical-setup/tabs/Medications/CreateMedication';
import { EditMedication } from '../components/medical-setup/tabs/Medications/EditMedication';
import { ViewMedication } from '../components/medical-setup/tabs/Medications/ViewMedication';
import { CreateProcedure } from '../components/medical-setup/tabs/Procedures/CreateProcedure';
import { EditProcedure } from '../components/medical-setup/tabs/Procedures/EditProcedure';
import { ViewProcedure } from '../components/medical-setup/tabs/Procedures/ViewProcedure';
import { EditPatientCondition } from '../components/patient/conditions/EditPatientCondition';
import { ViewPatientCondition } from '../components/patient/conditions/ViewPatientCondition';
import AddLaboratoryResult from '../components/patient/laboratory-results/AddLaboratoryResult';
import EditLaboratoryResult from '../components/patient/laboratory-results/EditLaboratoryResult';
import LaboratoryResultManualInput from '../components/patient/laboratory-results/LaboratoryResultManualInput';
import LaboratoryResultParseDocumentInput from '../components/patient/laboratory-results/LaboratoryResultParseDocumentInput';
import LaboratoryResults from '../components/patient/laboratory-results/LaboratoryResults';
import { Patient } from '../components/patient/Patient';
import ReferralsHubDetail from '../components/referrals/hub/ReferralsHubDetail';
import ReferralsHubList from '../components/referrals/hub/ReferralsHubList';
import { REFERRALS_HUB_PATH } from '../components/referrals/hub/referralHubPaths';
import ReferralForm from '../components/referrals/ReferralForm/ReferralForm';
import { RPMClaims } from '../components/rpms/claims/RPMClaims';
import { ClinicsConfigurations } from '../components/rpms/clinic-configuration/ClinicsConfiguration';
import { ExportedNotesList } from '../components/rpms/exported-notes-list/ExportedNotesList';
import { RPMs } from '../components/rpms/Rpms';
import { FeatureFlags } from '../lib/features';

export const customRoutes = (featureFlags: FeatureFlags) => [
  <Route exact path='/billing-export' component={BillingExporter} />,
  <Route exact path='/billing-export/new' component={BillingExporter} />,
  <Route exact path='/billing-export/history' component={BillingExportHistory} />,
  <Route exact path='/billing-export/history/:requestId' component={BillingExportHistory} />,
  <Route exact path='/billing-export/stats' component={BillingExportStats} />,
  ...(featureFlags.referralsCreation
    ? [<Route exact path='/referrals/create' component={ReferralForm} />]
    : []),
  ...(featureFlags.referralsHub
    ? [
        <Route exact path={REFERRALS_HUB_PATH} component={ReferralsHubList} />,
        <Route exact path={`${REFERRALS_HUB_PATH}/:referralId`} component={ReferralsHubDetail} />,
      ]
    : []),
  <Route
    exact
    path='/eligibility/insurance-plans/:insuranceId/show'
    component={InsurancePlansCreateOrEdit}
  />,
  <Route
    exact
    path='/eligibility/insurance-plans/payers/:payerId/create'
    component={InsurancePlansCreateOrEdit}
  />,
  <Route exact path='/eligibility/insurance-plans/create' component={InsurancePlansCreateOrEdit} />,
  <Route exact path='/eligibility/tasks/:leadId/show' component={TaskShowUpdate} />,
  <Route exact path='/eligibility/tasks/create' component={TaskShowUpdate} />,
  <Route exact path='/eligibility/payers/:payerId/show' component={PayersUpdate} />,
  <Route exact path='/eligibility/payers/create' component={PayersCreate} />,
  <Route exact path='/ace/import' component={ACEImporter} />,
  <Route exact path='/claims' component={Claims} />,
  <Route exact path='/claims/metrics' component={ClaimsMetricsPage} />,
  <Route exact path='/claims/:claimId/show' component={SingleClaim} />,
  <Route exact path='/rpms' component={RPMs} />,
  <Route exact path='/rpms/claims' component={RPMClaims} />,
  <Route exact path='/rpms/exported-notes-list' component={ExportedNotesList} />,
  <Route exact path='/rpms/clinics-configuration' component={ClinicsConfigurations} />,
  ...(featureFlags.medicalSetup
    ? [
        <Route exact path='/medical-setup' component={MedicalSetup} />,
        <Route exact path='/medical-setup/conditions' component={MedicalSetup} />,
        <Route
          exact
          path='/medical-setup/conditions/create'
          component={() => (
            <MedicalSetup>
              <CreateCondition />
            </MedicalSetup>
          )}
        />,
        <Route
          exact
          path='/medical-setup/conditions/:conditionId/show'
          component={() => (
            <MedicalSetup>
              <ViewCondition />
            </MedicalSetup>
          )}
        />,
        <Route
          exact
          path='/medical-setup/conditions/:conditionId/edit'
          component={() => (
            <MedicalSetup>
              <EditCondition />
            </MedicalSetup>
          )}
        />,
        <Route exact path='/medical-setup/procedures' component={MedicalSetup} />,
        <Route
          exact
          path='/medical-setup/procedures/create'
          component={() => (
            <MedicalSetup>
              <CreateProcedure />
            </MedicalSetup>
          )}
        />,
        <Route
          exact
          path='/medical-setup/procedures/:procedureId/show'
          component={() => (
            <MedicalSetup>
              <ViewProcedure />
            </MedicalSetup>
          )}
        />,
        <Route
          exact
          path='/medical-setup/procedures/:procedureId/edit'
          component={() => (
            <MedicalSetup>
              <EditProcedure />
            </MedicalSetup>
          )}
        />,
        <Route exact path='/medical-setup/laboratory-tests' component={MedicalSetup} />,
        <Route
          exact
          path='/medical-setup/laboratory-tests/create'
          component={() => (
            <MedicalSetup>
              <CreateLaboratoryTest />
            </MedicalSetup>
          )}
        />,
        <Route
          exact
          path='/medical-setup/laboratory-tests/:laboratoryTestId/show'
          component={() => (
            <MedicalSetup>
              <ViewLaboratoryTest />
            </MedicalSetup>
          )}
        />,
        <Route
          exact
          path='/medical-setup/laboratory-tests/:laboratoryTestId/edit'
          component={() => (
            <MedicalSetup>
              <EditLaboratoryTest />
            </MedicalSetup>
          )}
        />,
        <Route exact path='/medical-setup/medications' component={MedicalSetup} />,
        <Route
          exact
          path='/medical-setup/medications/create'
          component={() => (
            <MedicalSetup>
              <CreateMedication />
            </MedicalSetup>
          )}
        />,
        <Route
          exact
          path='/medical-setup/medications/:medicationId/show'
          component={() => (
            <MedicalSetup>
              <ViewMedication />
            </MedicalSetup>
          )}
        />,
        <Route
          exact
          path='/medical-setup/medications/:medicationId/edit'
          component={() => (
            <MedicalSetup>
              <EditMedication />
            </MedicalSetup>
          )}
        />,
      ]
    : []),

  ...(featureFlags.patientConditions
    ? [
        <Route exact path='/patients/conditions' component={Patient} />,
        <Route exact path='/patients' component={Patient} />,
        <Route
          exact
          path='/patients/:patientId/conditions/edit'
          component={() => (
            <Patient>
              <EditPatientCondition />
            </Patient>
          )}
        />,
        <Route
          exact
          path='/patients/:patientId/conditions/show'
          component={() => (
            <Patient>
              <ViewPatientCondition />
            </Patient>
          )}
        />,
      ]
    : []),
  ...(featureFlags.patientLabResults
    ? [
        <Route exact path='/laboratory-results' component={LaboratoryResults} />,
        <Route exact path='/laboratory-results/add' component={AddLaboratoryResult} />,
        <Route
          exact
          path='/laboratory-results/manual-input'
          component={LaboratoryResultManualInput}
        />,
        <Route
          exact
          path='/laboratory-results/parse-document-input'
          component={LaboratoryResultParseDocumentInput}
        />,
        <Route exact path='/laboratory-results/:groupId/edit' component={EditLaboratoryResult} />,
      ]
    : []),
  ...(featureFlags.auditLogs ? [<Route exact path='/audit-logs' component={AuditLogs} />] : []),
  ...(featureFlags.communicationsCenter
    ? [<Route path='/communications' component={CommunicationsCenter} />]
    : []),
  ...(featureFlags.documents
    ? [
        <Route exact path='/documents/library' component={Documents} />,
        <Route exact path='/documents/upload' component={DocumentUploader} />,
        <Route exact path='/documents/library/:documentId' component={DocumentDetails} />,
        <Route exact path='/documents/settings' component={DocumentSettings} />,
        <Route exact path='/documents/bulk-reprocess' component={BulkReprocess} />,
        <Route
          exact
          path='/documents/settings/document-types/:id'
          component={DocumentTypeDetail}
        />,
        <Route exact path='/documents/settings/llm-prompts/:id' component={LlmPromptDetail} />,
      ]
    : []),
  ...(featureFlags.coupons ? [<Route exact path='/coupons' component={CouponsManager} />] : []),
];
